import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ outlet_id: "", supplier: "", note: "", items: [] });

  const load = async () => {
    const [p, prods, o] = await Promise.all([api.get("/purchases"), api.get("/products"), api.get("/outlets")]);
    setPurchases(p.data || []); setProducts(prods.data || []); setOutlets(o.data || []);
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", name: "", qty: 1, cost: 0 }] }));
  const updateItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((x, idx) => idx===i ? { ...x, [k]: v, ...(k==="product_id"?{name: products.find(p=>p.id===v)?.name || "", cost: products.find(p=>p.id===v)?.cost || 0}:{}) } : x) }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.outlet_id || form.items.length === 0) { toast.error("Lengkapi data"); return; }
    try {
      const items = form.items.map((it) => ({ ...it, qty: Number(it.qty), cost: Number(it.cost), subtotal: Number(it.qty) * Number(it.cost) }));
      await api.post("/purchases", { ...form, items });
      toast.success("Pembelian dicatat. Stok bertambah."); setOpen(false); setForm({ outlet_id: "", supplier: "", note: "", items: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">PEMBELIAN</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Pembelian / PO</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="purchase-add-btn" className="px-4 py-2.5 text-sm bg-[#002FA7] text-white font-semibold inline-flex items-center gap-1"><Plus className="h-4 w-4"/>Catat Pembelian</button>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Waktu</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-left p-3 overline">Supplier</th>
            <th className="text-left p-3 overline">Item</th>
            <th className="text-right p-3 overline">Total</th>
          </tr></thead>
          <tbody>
            {purchases.length===0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Belum ada pembelian</td></tr>}
            {purchases.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-zinc-50">
                <td className="p-3 text-xs text-zinc-500">{formatDate(p.created_at)}</td>
                <td className="p-3">{outlets.find(o=>o.id===p.outlet_id)?.name || "-"}</td>
                <td className="p-3">{p.supplier||"-"}</td>
                <td className="p-3 text-zinc-500">{p.items.length} item</td>
                <td className="p-3 text-right tabular-nums font-semibold">{formatIDR(p.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-3xl">
          <DialogHeader><DialogTitle>Catat Pembelian</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline">Outlet</label>
              <select data-testid="po-outlet" value={form.outlet_id} onChange={(e)=>setForm({...form,outlet_id:e.target.value})} className="w-full border border-border px-3 py-2 mt-1">
                <option value="">— pilih —</option>{outlets.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div><label className="overline">Supplier</label><input data-testid="po-supplier" value={form.supplier} onChange={(e)=>setForm({...form,supplier:e.target.value})} className="w-full border border-border px-3 py-2 mt-1"/></div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2"><div className="overline">Item</div><button onClick={addItem} data-testid="po-add-item" className="text-xs border border-border px-3 py-1.5">+ Item</button></div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <select value={it.product_id} onChange={(e)=>updateItem(i,"product_id",e.target.value)} className="col-span-6 border border-border px-2 py-2 text-sm" data-testid={`po-item-${i}-product`}>
                    <option value="">— produk —</option>{products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <input type="number" value={it.qty} onChange={(e)=>updateItem(i,"qty",e.target.value)} className="col-span-2 border border-border px-2 py-2 text-sm font-mono-num" placeholder="Qty" data-testid={`po-item-${i}-qty`}/>
                  <input type="number" value={it.cost} onChange={(e)=>updateItem(i,"cost",e.target.value)} className="col-span-3 border border-border px-2 py-2 text-sm font-mono-num" placeholder="HPP" data-testid={`po-item-${i}-cost`}/>
                  <button onClick={()=>removeItem(i)} className="col-span-1 p-2 hover:bg-rose-50 text-rose-600"><X className="h-4 w-4"/></button>
                </div>
              ))}
              {form.items.length===0 && <div className="text-xs text-zinc-500">Tambahkan item.</div>}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={()=>setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={save} data-testid="po-save-btn" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
